import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LocationService, MapsService, Sublocation } from '@uzurv/clara';
import { of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as LocationManagerActions from './location-manager.actions';
import * as LocationManagerSelectors from './location-manager.selectors';
import { AlertDialogComponent } from '../../components/dialogs/alert-dialog/alert-dialog.component';

@Injectable()
export class LocationManagerEffects {
  getAutocompleteResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationManagerActions.Search),
      switchMap(action =>
        this.mapsService.autocomplete(action.searchString).pipe(
          map(autoCompleteResults => LocationManagerActions.SearchResultsLoaded({ results: autoCompleteResults })),
          catchError(error => of(LocationManagerActions.LoadSearchResultsFailed({ error })))
        )
      )
    );
  });

  getLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationManagerActions.SearchResultSelected),
      switchMap(action =>
        this.locationService.addLocation(action.locationLabel).pipe(
          map(location => LocationManagerActions.LocationLoaded({ location })),
          catchError(error => of(LocationManagerActions.LoadLocationFailed({ error })))
        )
      )
    );
  });

  submitNewLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationManagerActions.SubmitNewLocation),
      withLatestFrom(this.store$.select(LocationManagerSelectors.locationManagerState)),
      switchMap(([_, state]) => {
        const sublocation = new Sublocation();
        sublocation.locationId = state.loadedLocation.id;
        sublocation.name = state.newLocationName;
        sublocation.latitude = state.mapCenterCoords.lat;
        sublocation.longitude = state.mapCenterCoords.lng;
        sublocation.description = null;
        return this.locationService.addSublocation(state.loadedLocation.id, sublocation).pipe(
          map(sub => LocationManagerActions.NewLocationAccepted({ sublocation: sub })),
          catchError(error => of(LocationManagerActions.SubmitNewLocationFailed({ error })))
        );
      })
    );
  });

  deleteSublocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationManagerActions.DeleteSublocation),
      switchMap(action =>
        this.locationService.deleteSublocation(action.sublocation.id).pipe(
          map(() => LocationManagerActions.DeleteSublocationSuccess()),
          catchError(error => of(LocationManagerActions.DeleteSublocationFailed({ error })))
        )
      )
    );
  });

  getUserLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationManagerActions.GetUserLocation),
      switchMap(() =>
        this.mapsService.getUserLocation().pipe(
          map(position => LocationManagerActions.GetUserLocationSuccess({ coordinates: position })),
          catchError(error => of(LocationManagerActions.GetUserLocationFailed({ error })))
        )
      )
    )
  );

  geocodeOffsetLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationManagerActions.MoveMap),
      debounceTime(500),
      switchMap(action =>
        this.mapsService.geocodeLocationByLatLng(action.coords.lat, action.coords.lng).pipe(
          map(location => LocationManagerActions.GeocodeOffsetLocationSuccess({ location })),
          catchError(error => of(LocationManagerActions.GeocodeOffsetLocationFailed({ error })))
        )
      )
    )
  );

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          LocationManagerActions.LoadSearchResultsFailed,
          LocationManagerActions.LoadLocationFailed,
          LocationManagerActions.NewLocationInvalid,
          LocationManagerActions.SubmitNewLocationFailed,
          LocationManagerActions.DeleteSublocationFailed
        ),
        map(action => {
          let errorMessage = action.error.message;

          if (action.type === LocationManagerActions.LoadLocationFailed.type && errorMessage.includes('SQL')) {
            errorMessage = `That doesn't seem to be a valid location; please try searching a more specific address`;
          }

          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Error',
              message: errorMessage
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  loading$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          LocationManagerActions.SearchResultSelected,
          LocationManagerActions.SubmitNewLocation,
          LocationManagerActions.DeleteSublocation
        )
      );
    },
    { dispatch: false }
  );

  doneLoading$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          LocationManagerActions.LocationLoaded,
          LocationManagerActions.LoadLocationFailed,
          LocationManagerActions.NewLocationAccepted,
          LocationManagerActions.SubmitNewLocationFailed,
          LocationManagerActions.DeleteSublocationSuccess,
          LocationManagerActions.DeleteSublocationFailed
        )
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store$: Store,
    private mapsService: MapsService,
    private locationService: LocationService,
    private dialog: MatDialog
  ) {}
}
